import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isAuthenticated } from '../lib/authUtils';
import { fetchCart, addCartItem, reduceCartItem, deleteCartItem, clearRemoteCart } from '../services/cartService';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cart from backend for authenticated users, or localStorage for guests
  const loadCart = useCallback(async () => {
    if (isAuthenticated()) {
      try {
        setLoading(true);
        const serverCart = await fetchCart();
        setCart(serverCart);
        // Also update localStorage as backup
        localStorage.setItem('cart', JSON.stringify(serverCart));
      } catch (error) {
        console.error('Error fetching cart from server:', error);
        // Fallback to localStorage
        const savedCart = localStorage.getItem('cart');
        if (savedCart) setCart(JSON.parse(savedCart));
      } finally {
        setLoading(false);
      }
    } else {
      // Guest user - use localStorage
      const savedCart = localStorage.getItem('cart');
      if (savedCart) setCart(JSON.parse(savedCart));
    }
  }, []);

  // Load cart and other data on mount
  useEffect(() => {
    loadCart();
    
    const savedWishlist = localStorage.getItem('wishlist');
    const savedCompare = localStorage.getItem('compareList');
    
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
    if (savedCompare) setCompareList(JSON.parse(savedCompare));
  }, [loadCart]);

  // Listen for auth state changes to reload cart
  useEffect(() => {
    const handleAuthChange = () => {
      loadCart();
    };
    window.addEventListener('authStateChanged', handleAuthChange);
    return () => window.removeEventListener('authStateChanged', handleAuthChange);
  }, [loadCart]);

  // Save to localStorage whenever cart changes (as backup)
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem('compareList', JSON.stringify(compareList));
  }, [compareList]);

  const addToCart = async (product, quantity = 1) => {
    // Optimistically update local state
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, quantity }];
    });

    // Sync with backend if authenticated
    if (isAuthenticated()) {
      try {
        await addCartItem({
          productId: product.id,
          productPrice: product.price,
          quantity: quantity,
        });
        // Optionally reload cart to get server state
        // await loadCart();
      } catch (error) {
        console.error('Error adding to cart on server:', error);
        // Cart is already updated locally, so user experience is preserved
      }
    }
  };

  const removeFromCart = async (productId) => {
    // Find the cart item to get cartId
    const cartItem = cart.find(item => item.id === productId);
    
    // Optimistically update local state
    setCart(prevCart => prevCart.filter(item => item.id !== productId));

    // Sync with backend if authenticated
    if (isAuthenticated() && cartItem?.cartId) {
      try {
        await deleteCartItem(cartItem.cartId);
      } catch (error) {
        console.error('Error removing from cart on server:', error);
      }
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const cartItem = cart.find(item => item.id === productId);
    const oldQuantity = cartItem?.quantity || 0;
    const quantityDiff = newQuantity - oldQuantity;

    // Optimistically update local state
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );

    // Sync with backend if authenticated
    if (isAuthenticated() && cartItem) {
      try {
        if (quantityDiff > 0) {
          // Adding more items
          await addCartItem({
            productId: productId,
            productPrice: cartItem.price,
            quantity: quantityDiff,
          });
        } else if (quantityDiff < 0) {
          // Reducing items
          await reduceCartItem({
            productId: productId,
            productPrice: cartItem.price,
            quantity: Math.abs(quantityDiff),
          });
        }
      } catch (error) {
        console.error('Error updating quantity on server:', error);
      }
    }
  };

  const clearCart = async () => {
    const cartItems = [...cart];
    
    // Optimistically clear local state
    setCart([]);

    // Sync with backend if authenticated
    if (isAuthenticated()) {
      try {
        await clearRemoteCart(cartItems);
      } catch (error) {
        console.error('Error clearing cart on server:', error);
      }
    }
  };

  // Refresh cart from server (useful after login or order placement)
  const refreshCart = async () => {
    await loadCart();
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  // Wishlist functions
  const addToWishlist = (product) => {
    setWishlist(prevWishlist => {
      if (prevWishlist.find(item => item.id === product.id)) {
        return prevWishlist;
      }
      return [...prevWishlist, product];
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlist(prevWishlist => prevWishlist.filter(item => item.id !== productId));
  };

  const isInWishlist = (productId) => {
    return wishlist.some(item => item.id === productId);
  };

  // Compare functions
  const addToCompare = (product) => {
    setCompareList(prevCompare => {
      if (prevCompare.find(item => item.id === product.id)) {
        return prevCompare;
      }
      if (prevCompare.length >= 4) {
        alert('You can only compare up to 4 products at a time');
        return prevCompare;
      }
      return [...prevCompare, product];
    });
  };

  const removeFromCompare = (productId) => {
    setCompareList(prevCompare => prevCompare.filter(item => item.id !== productId));
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  const isInCompare = (productId) => {
    return compareList.some(item => item.id === productId);
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    refreshCart,
    getCartTotal,
    getCartCount,
    wishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    compareList,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isInCompare
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
